// hooks/useAudioPlayer.ts
// Custom hook for audio playback management

import { useRef, useEffect, useState } from 'react'

interface UseAudioPlayerProps {
  audioUrl: string | undefined | null
}

export function useAudioPlayer({ audioUrl }: UseAudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  // Initialize audio object when URL changes
  useEffect(() => {
    if (audioUrl) {
      audioRef.current = new Audio(audioUrl)

      // Update playing state when audio ends
      const audio = audioRef.current
      const handleEnded = () => setIsPlaying(false)
      audio.addEventListener('ended', handleEnded)

      return () => {
        audio.removeEventListener('ended', handleEnded)
        audio.pause()
        audio.currentTime = 0
      }
    } else {
      console.warn('Audio URL not provided to useAudioPlayer')
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }
    }
  }, [audioUrl])

  const play = () => {
    const audio = audioRef.current
    if (!audio) return

    audio.currentTime = 0
    audio.play()
      .then(() => setIsPlaying(true))
      .catch(err => {
        console.error('Audio playback failed:', err)
        setIsPlaying(false)
      })
  }

  const pause = () => {
    const audio = audioRef.current
    if (!audio) return

    audio.pause()
    audio.currentTime = 0
    setIsPlaying(false)
  }

  const toggle = () => {
    const audio = audioRef.current
    if (!audio) return

    if (!audio.paused) {
      // Currently playing - stop it
      pause()
    } else {
      // Currently paused - play it
      play()
    }
  }

  const stop = () => {
    pause()
  }

  return {
    audioRef,
    isPlaying,
    play,
    pause,
    toggle,
    stop
  }
}
